import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { BPTestingProjectResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

interface FormDataT {
  name: string;
  source_url: string;
  owner: string;
  channel_id: null | number;
  note: string;
}

interface Props {
  onClose: () => void;
  editableProject?: BPTestingProjectResource;
}

const BPTestingProjectForm: FC<Props> = ({ onClose, editableProject }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const userName = useSelector((state: RootState) => state.auth.user!.name);

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      owner: editableProject?.owner ?? userName,
      name: editableProject?.name ?? '',
      note: editableProject?.note ?? '',
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        if (editableProject) {
          await api.put(
            `/v1/editorial/bp-testing-projects/${editableProject.id}`,
            data,
          );
        } else {
          await api.post(`/v1/editorial/bp-testing-projects`, data);
        }

        await queryClient.invalidateQueries(['bp_testing_projects']);

        onClose();

        alerts.success('A new project has been successfully created');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={editableProject ? 'Edit Project' : 'New Project'}
        onClose={onClose}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                label="Project Name"
                control={control}
                name="name"
              />
              <FormField<FormDataT>
                label="Owner"
                control={control}
                name="owner"
              />
              <FormField<FormDataT>
                label="Note"
                control={control}
                name="note"
                type="textarea"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {editableProject ? 'Update' : 'Create'}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default BPTestingProjectForm;
